import { isEditPage, isRelistPage, isCreatePage } from './../../../helpers/router.helper';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { DraftFacade } from '@classifieds-sell/state';
import { PropertySectionBase } from '../../property-section-base';
import { PremiumPackage } from 'libs/datalayer/src';
import { StepIds } from '../../../models/property-step/property-step-model';
import { ActivatedRoute } from '@angular/router';
import { isBasePackageSelection } from '../../../helpers/price.helper';
import { BASE_PACKAGE_CODE_KEY, DEFAULT_CONTACT_SUPPORT_EMAIL } from '../../../constants/property-promote.constants';

@Component({
    selector: 'property-agent-promote-section',
    templateUrl: './property-agent-promote-section.component.html',
    styleUrls: ['./property-agent-promote-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class PropertyAgentPromoteSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('propertyAgentPromoteSectionForm')
    propertyAgentPromoteSectionForm: NgForm;

    packages: PremiumPackage[];
    selectedPackage: PremiumPackage;
    packageName: string;
    displayError: boolean;
    stepIds = StepIds;
    basePackage: PremiumPackage;
    emptyPackage: PremiumPackage;
    basePackageSelection: boolean;
    baseKey = BASE_PACKAGE_CODE_KEY;
    defaultContactSupportEmail = DEFAULT_CONTACT_SUPPORT_EMAIL;
    isEditPage: boolean;
    isRelistPage: boolean;
    isCreatePage: boolean;
    paPackageSelected: boolean;
    private _defaultingPaPackage = false;
    private _editPageBasePackageCodeChange = false;
    constructor(
        draftFacade: DraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _route: ActivatedRoute
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this.isEditPage = isEditPage(this._route);
        this.isRelistPage = isRelistPage(this._route);
        this.isCreatePage = isCreatePage(this._route);
        this.paPackageSelected = false;
        this.packages = [];
        this.emptyPackage = {
            name: '',
            code: '',
            priority: 0
        };
        this.selectedPackage = this.emptyPackage;

        super.onInit(() => {
            this._changeDetectorRef.markForCheck();
            this.packages = this.metadata.premiumPackages
                .filter((a) => a.code !== this.baseKey)
                .slice()
                .sort((a, b) => b.priority - a.priority);
            this.basePackage = this.metadata.premiumPackages.find(
                (a) => a.code === this.baseKey
            );
            this.basePackageSelection = isBasePackageSelection(this.draftData.propertyPromote.packageCode);
            // base listing checkbox can ONLY gain disabled state
            // on edit flow when agent (none PA specifically) has selected a package.
            // this variable is used to track if the base listing checkbox has been changed
            // during draft data publish() in edit flow
            // NOTE: the state tracking for checkbox and package code looks ugly
            // possibly due to the publish() and angular change detection does not play well
            this._editPageBasePackageCodeChange = this.isEditPage
                && this.originalDraftData.propertyPromote.packageCode === this.baseKey
                && this.draftData.propertyPromote.packageCode !== this.originalDraftData.propertyPromote.packageCode;
            if (this.draftData.propertyPromote.packageCode) {
                this.selectedPackage = this.metadata.premiumPackages.find(
                    (p) => p.code === this.draftData.propertyPromote.packageCode
                );
            }

            if (this.hasPartnershipAgreement()) {
                const paPackageCode = this.metadata.partnershipAgreementContents.premiumPackages[0].code; // taking the first package;

                if (this.isCreatePage && this.selectedPackage?.code !== paPackageCode) {
                    this.selectedPackage = this.metadata.premiumPackages.find(
                        p => p.code === paPackageCode
                    );
                    this._defaultingPaPackage = true;
                    this.selectPackage();
                    this.paPackageSelected = true;
                } else {
                    this.paPackageSelected = this.isCreatePage
                        || (this.originalDraftData.propertyPromote?.packageCode
                            && this.originalDraftData.propertyPromote?.packageCode === paPackageCode);
                }
            }

            if (!this.selectedPackage) {
                // a check for if an agent switches between having a PA and not having a PA
                this.selectedPackage = this.emptyPackage;
            }

            if (!this.selectedPackage.code || (this.hasPartnershipAgreement() && this._defaultingPaPackage)) {
                this.propertyAgentPromoteSectionForm?.form.markAsDirty();
            }
        });
    }

    isValid(): boolean {
        if (this.usePartnershipAgreement()) {
            return true;
        }

        this.displayError = !(
            this.draftData.propertyPromote.packageCode !== null &&
            this.draftData.propertyPromote.packageCode !== ''
        );
        this._changeDetectorRef.markForCheck();
        return !this.displayError;
    }

    dirty(): boolean {
        return this.propertyAgentPromoteSectionForm.form.dirty;
    }

    hasPartnershipAgreement(): boolean {
        return (this.metadata.partnershipAgreementContents?.premiumPackages || [])
                .length > 0;
    }

    usePartnershipAgreement(): boolean {
        return (this.isCreatePage && this.hasPartnershipAgreement())
            || (this.isEditPage && this.paPackageSelected);
    }

    onSelectPackage(packageCode: string): void {
        this.selectedPackage = this.packages.find(
            (p) => p.code === packageCode
        );
        this.selectPackage();
    }

    onBasePackageSelection(value: boolean): void {
        this.selectedPackage = value
        ? {
            name: this.basePackage.name,
            code: this.baseKey,
            priority: this.basePackage.priority
        }
        : this.emptyPackage;

        this.selectPackage();
        if (this.isEditPage){
            return;
        }
        this.resetValueForPackageCodeWhenBasePackageSelectionChange(value);
    }

    private resetValueForPackageCodeWhenBasePackageSelectionChange(basePackageSelection: boolean): void {
        if (!basePackageSelection) {
            this.draftData.propertyPromote.packageCode = this.originalDraftData.propertyPromote.packageCode;
        }
    }

    selectPackage() {
        this.draftData.propertyPromote.packageCode = this.selectedPackage.code;
        this.propertyAgentPromoteSectionForm?.form.markAsDirty();
        this.displayError = false;
        this.publish();
        this._changeDetectorRef.markForCheck();
    }

    isDisablePackage(premiumPackage: PremiumPackage) {
        return (this.selectedPackage.code === this.baseKey || this.isEditPage)
            && premiumPackage.code !== this.selectedPackage.code;
    }

    userHasSelectedNonBasePackage(): boolean {
        return this.selectedPackage.code && this.selectedPackage.code !== this.baseKey;
    }

    userHasSelectedBasePackage(): boolean {
        return this.selectedPackage.code && this.selectedPackage.code === this.baseKey;
    }

    shouldDisableCards(){
        return (this.isEditPage && !this._editPageBasePackageCodeChange)
            || (!this.isEditPage && this.userHasSelectedBasePackage());
    }
    shouldDisableCheckboxes(){
        return this.isEditPage && !this._editPageBasePackageCodeChange
            && this.userHasSelectedNonBasePackage();
    }
    shouldShowInfobox() {
        return this.isEditPage && this.originalDraftData.propertyPromote.packageCode !== this.baseKey;
    }
}
